import apiClient from '@/lib/apiClient';
import type { EmailPreviewResult, SendEmailResult } from '@/services/email.service';
import { ApiResponse } from '@/types';
import type { BillingStatusValue } from '@/utils/constants';

// ─── DTOs (mirror server/src/services/billing.service.ts) ────────────────────
export interface BillingProductLine {
  name: string;
  grossAmount: number;
  discountLabel: string;
  netAmount: number;
  duration: string;
}

export interface BillingInstallmentRow {
  installmentNumber: number;
  amount: number;
  dueDate: string;
  transactionNo: string | null;
  mode: string | null;
  paid: boolean;
  handedOverAt: string | null;
  // 'Billed' | 'Due' | 'Not Billed'
  billingLabel: string;
  isCurrent: boolean;
}

export interface BillingState {
  status: BillingStatusValue;
  /**
   * This installment reached billing UNPAID, marked 'Due' (ADR-053) - service
   * starts on a promise. There is nothing to invoice and nothing to send, so
   * Generate Invoice and Send Invoice To Client are skipped: verify, notify the
   * service team, complete.
   */
  dueBilling: boolean;
  /**
   * Whether this installment notifies the Service team. True only for the
   * FIRST: service is activated once per order, so installments 2 and 3 skip
   * that step and raise no service queue row.
   */
  notifiesService: boolean;
  verifiedAt: string | null;
  verifiedByName: string | null;
  verifyNotes: string | null;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  invoiceAmount: number | null;
  invoicePdfName: string | null;
  hasInvoicePdf: boolean;
  invoiceRecordedAt: string | null;
  invoiceRecordedByName: string | null;
  serviceNotifiedAt: string | null;
  serviceNotifiedByName: string | null;
  serviceQueueRef: string | null;
  clientInvoiceSentAt: string | null;
  clientInvoiceSentByName: string | null;
  completedAt: string | null;
  completedByName: string | null;
}

// ─── Editable verification data (ADR-046) ────────────────────────────────────
export interface BillingEditableClient {
  arnHolderName: string;
  legalName: string;
  displayName: string;
  gstin: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  mobile: string;
  email: string;
  arn: string;
}

export interface BillingEditableSales {
  selectedBankLabel: string;
  renewalFrequency: string;
  paymentMode: string;
}

export interface BillingEditableProduct {
  productCode: string;
  productName: string;
  customPrice: number | null;
  termYears: number | null;
  discountMode: 'amount' | 'percent' | null;
  discountValue: number | null;
  termOptions: number[];
}

export interface BillingEditableInstallment {
  installmentNumber: number;
  amount: number;
  dueDate: string;
  transactionNo: string;
  mode: string;
}

export interface BillingEditableData {
  client: BillingEditableClient;
  sales: BillingEditableSales;
  products: BillingEditableProduct[];
  installments: BillingEditableInstallment[];
  bankOptions: string[];
  payableTotal: number | null;
  canEdit: boolean;
}

export interface BillingCorrectionEntry {
  field: string;
  label: string;
  from: string;
  to: string;
  at: string;
  byId: number;
  notifiedAt: string | null;
}

export interface BillingCorrectionState {
  entries: BillingCorrectionEntry[];
  pendingCount: number;
  notifiedAt: string | null;
  notifiedByName: string | null;
}

// Partial patch of the four verification blocks. Only the sections/fields the
// biller actually touched are sent, so an untouched field is never rewritten.
export interface BillingVerificationPatch {
  client?: Partial<BillingEditableClient>;
  sales?: Partial<BillingEditableSales>;
  products?: Array<{
    productCode: string;
    customPrice?: number | null;
    termYears?: number | null;
    discountMode?: 'amount' | 'percent' | null;
    discountValue?: number | null;
  }>;
  installments?: Array<{
    installmentNumber: number;
    amount: number;
    dueDate: string;
    transactionNo?: string | null;
    mode?: string | null;
  }>;
}

export interface BillingClientListItem {
  // The LEAD id. The row's real identity is (id, orderId) - see below.
  id: number;
  // The ORDER this row bills. The queue is one row per order (ADR-048), so
  // every workflow call made from a row must carry this or the server picks an
  // order itself and the biller ends up acting on a different one.
  orderId: number;
  // Per-client sequence (1, 2, 3...) and Initial / Add-on, for the row label.
  orderNumber: number;
  orderType: string;
  leadCode: number | null;
  arn: string;
  clientName: string;
  businessName: string;
  email: string;
  mobile: string;
  rmName: string;
  total: number;
  amountPaid: number;
  pendingAmount: number;
  paymentStatus: string;
  billingStatus: BillingStatusValue;
  actionRequired: boolean;
  // Claim-based assignment: the biller who claimed the current installment's
  // billing task (null = unclaimed / free to pick up) and their display name.
  assignedToId: number | null;
  assignedToName: string | null;
  handedOverAt: string | null;
  onboardedAt: string | null;
}

export interface BillingClientDetail extends BillingClientListItem {
  gstin: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  rmId: number;
  rmEmail: string;
  paymentMode: string | null;
  selectedBankLabel: string | null;
  quotationReference: string | null;
  // Renewal cadence chosen by the Sales RM in the payment plan step; shown
  // read-only during billing verification.
  renewalFrequency: string;
  products: BillingProductLine[];
  subtotal: number;
  discountLabel: string;
  discountAmount: number;
  gstPercentage: number;
  gstAmount: number;
  installments: BillingInstallmentRow[];
  currentInstallmentNumber: number;
  currentInstallmentAmount: number;
  hasPendingInstallment: boolean;
  state5: BillingState;
  editable: BillingEditableData;
  correction: BillingCorrectionState;
}

export interface BillingMetrics {
  pendingVerification: number;
  invoicePending: number;
  serviceNotificationPending: number;
  clientInvoicePending: number;
  partiallyBilled: number;
  completed: number;
  total: number;
  totalCollectable: number;
  totalCollected: number;
  actionRequired: BillingClientListItem[];
}

// Claim-based (pull) queue filter: my claimed tasks, the free pool, or all.
export type BillingAssignmentFilter = 'mine' | 'unassigned' | 'all';

// ─── Dashboard analytics (mirror server getBillingDashboard) ─────────────────
export type BillingDashboardScope = 'all' | 'mine';
export type BillingDashboardPeriod = 'all' | 'today' | 'week' | 'month';

export interface BillingFunnel {
  verification: number;
  invoiceGeneration: number;
  serviceNotification: number;
  invoiceSent: number;
  partiallyBilled: number;
  completed: number;
}

export interface BillingPerformance {
  avgBillingCompletionDays: number | null;
  avgInvoiceGenerationHrs: number | null;
  avgServiceHandoverHrs: number | null;
  avgClientInvoiceDeliveryHrs: number | null;
  billingSuccessRate: number;
}

export interface BillingRevenueRow {
  month: string;
  invoices: number;
  billingAmount: number;
  completed: number;
  pending: number;
}

export interface BillingActivity {
  id: string;
  kind: 'verified' | 'invoice' | 'notify' | 'sent' | 'completed';
  label: string;
  clientName: string;
  byName: string | null;
  amount: number | null;
  at: string;
}

export interface BillingDashboard {
  funnel: BillingFunnel;
  performance: BillingPerformance;
  revenue: BillingRevenueRow[];
  recentActivities: BillingActivity[];
}

export interface BillingDashboardParams {
  scope?: BillingDashboardScope;
  period?: BillingDashboardPeriod;
}

export interface BillingListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  assignment?: BillingAssignmentFilter;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface BillingListResponse {
  rows: BillingClientListItem[];
  total: number;
  totalPages: number;
  currentPage: number;
}

export interface RecordInvoicePayload {
  invoiceNumber: string;
  invoiceDate: string;
  invoiceAmount: number;
  pdfBase64: string;
  pdfName: string;
}

export interface SendInvoicePayload {
  templateSlug: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  bodyHtml: string;
  bodyText: string;
  fromEmail?: string;
}

// Internal team-to-team notice (correction summary to the Sales RM, service
// activation notice to the Service team). The template slug is pinned server-
// side, so it is deliberately absent here.
export type SendInternalNoticePayload = Omit<SendInvoicePayload, 'templateSlug'>;

export type BillingSendResult = { detail: BillingClientDetail; email: SendEmailResult };

// Reads a File as a bare base64 string (no data: prefix) for the JSON upload.
export const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

const toParams = (params: BillingListParams): Record<string, string | number> => {
  const out: Record<string, string | number> = {};
  if (params.page) out.page = params.page;
  if (params.limit) out.limit = params.limit;
  if (params.search) out.search = params.search;
  if (params.status) out.status = params.status;
  if (params.assignment && params.assignment !== 'all') out.assignment = params.assignment;
  if (params.sortBy) out.sortBy = params.sortBy;
  if (params.sortDir) out.sortDir = params.sortDir;
  return out;
};

export const billingService = {
  list: async (params: BillingListParams = {}): Promise<BillingListResponse> => {
    const { data } = await apiClient.get<ApiResponse<BillingClientListItem[]>>('/billing/clients', {
      params: toParams(params),
    });
    const meta = data.meta as
      { total?: number; totalPages?: number; currentPage?: number } | undefined;
    return {
      rows: data.data ?? [],
      total: meta?.total ?? 0,
      totalPages: meta?.totalPages ?? 1,
      currentPage: meta?.currentPage ?? 1,
    };
  },

  metrics: async (): Promise<BillingMetrics> => {
    const { data } = await apiClient.get<ApiResponse<BillingMetrics>>('/billing/metrics');
    if (!data.data) throw new Error('Billing metrics not found in response');
    return data.data;
  },

  dashboard: async (params: BillingDashboardParams = {}): Promise<BillingDashboard> => {
    const query: Record<string, string> = {};
    if (params.scope && params.scope !== 'all') query.scope = params.scope;
    if (params.period && params.period !== 'all') query.period = params.period;
    const { data } = await apiClient.get<ApiResponse<BillingDashboard>>('/billing/dashboard', {
      params: query,
    });
    if (!data.data) throw new Error('Billing dashboard not found in response');
    return data.data;
  },

  getById: async (id: number, orderId?: number): Promise<BillingClientDetail> => {
    const { data } = await apiClient.get<ApiResponse<BillingClientDetail>>(
      `/billing/clients/${id}`,
      { params: { orderId } },
    );
    if (!data.data) throw new Error('Billing client not found in response');
    return data.data;
  },

  // Claim (pull) the current installment's billing task so the biller works it
  // exclusively; release hands it back to the unassigned pool.
  claim: async (id: number, orderId?: number): Promise<BillingClientDetail> => {
    const { data } = await apiClient.post<ApiResponse<BillingClientDetail>>(
      `/billing/clients/${id}/claim`,
      undefined,
      { params: { orderId } },
    );
    if (!data.data) throw new Error('Billing client not found in response');
    return data.data;
  },

  release: async (id: number, orderId?: number): Promise<BillingClientDetail> => {
    const { data } = await apiClient.post<ApiResponse<BillingClientDetail>>(
      `/billing/clients/${id}/release`,
      undefined,
      { params: { orderId } },
    );
    if (!data.data) throw new Error('Billing client not found in response');
    return data.data;
  },

  // Corrects the data the Sales RM handed over (ADR-046). Allowed until this
  // installment is verified; writes through to the canonical lead tables.
  updateVerificationData: async (
    id: number,
    patch: BillingVerificationPatch,
    orderId?: number,
  ): Promise<BillingClientDetail> => {
    const { data } = await apiClient.put<ApiResponse<BillingClientDetail>>(
      `/billing/clients/${id}/verification-data`,
      patch,
      { params: { orderId } },
    );
    if (!data.data) throw new Error('Billing client not found in response');
    return data.data;
  },

  previewCorrectionEmail: async (id: number, orderId?: number): Promise<EmailPreviewResult> => {
    const { data } = await apiClient.get<ApiResponse<EmailPreviewResult>>(
      `/billing/clients/${id}/correction-preview`,
      { params: { orderId } },
    );
    if (!data.data) throw new Error('Correction email preview not found in response');
    return data.data;
  },

  sendCorrection: async (
    id: number,
    payload: SendInternalNoticePayload,
    orderId?: number,
  ): Promise<BillingSendResult> => {
    const { data } = await apiClient.post<ApiResponse<BillingSendResult>>(
      `/billing/clients/${id}/send-correction`,
      payload,
      { timeout: 60_000, params: { orderId } },
    );
    if (!data.data) throw new Error('Send result not found in response');
    return data.data;
  },

  verify: async (id: number, notes?: string, orderId?: number): Promise<BillingClientDetail> => {
    const { data } = await apiClient.post<ApiResponse<BillingClientDetail>>(
      `/billing/clients/${id}/verify`,
      { ...(notes ? { notes } : {}) },
      { params: { orderId } },
    );
    if (!data.data) throw new Error('Billing client not found in response');
    return data.data;
  },

  recordInvoice: async (
    id: number,
    payload: RecordInvoicePayload,
    orderId?: number,
  ): Promise<BillingClientDetail> => {
    const { data } = await apiClient.post<ApiResponse<BillingClientDetail>>(
      `/billing/clients/${id}/invoice`,
      payload,
      { timeout: 60_000, params: { orderId } },
    );
    if (!data.data) throw new Error('Billing client not found in response');
    return data.data;
  },

  // Preview of the internal service-activation notice. Also mints the service
  // queue reference server-side so the preview shows the real one.
  previewServiceNotification: async (id: number, orderId?: number): Promise<EmailPreviewResult> => {
    const { data } = await apiClient.get<ApiResponse<EmailPreviewResult>>(
      `/billing/clients/${id}/service-preview`,
      { params: { orderId } },
    );
    if (!data.data) throw new Error('Service notification preview not found in response');
    return data.data;
  },

  // Emails the Service team, then stamps the notify step. The email IS the
  // notification - there is no silent "mark as notified" path.
  notifyService: async (
    id: number,
    payload: SendInternalNoticePayload,
    orderId?: number,
  ): Promise<BillingSendResult> => {
    const { data } = await apiClient.post<ApiResponse<BillingSendResult>>(
      `/billing/clients/${id}/notify-service`,
      payload,
      { timeout: 60_000, params: { orderId } },
    );
    if (!data.data) throw new Error('Send result not found in response');
    return data.data;
  },

  // Billing-scoped preview: shows the SAME invoice number/date/total that will
  // actually be sent (built from the recorded invoice, not the live quotation).
  previewInvoiceEmail: async (id: number, orderId?: number): Promise<EmailPreviewResult> => {
    const { data } = await apiClient.get<ApiResponse<EmailPreviewResult>>(
      `/billing/clients/${id}/invoice-preview`,
      { params: { orderId } },
    );
    if (!data.data) throw new Error('Invoice email preview not found in response');
    return data.data;
  },

  sendInvoice: async (
    id: number,
    payload: SendInvoicePayload,
    orderId?: number,
  ): Promise<{ detail: BillingClientDetail; email: SendEmailResult }> => {
    type SendResult = { detail: BillingClientDetail; email: SendEmailResult };
    const { data } = await apiClient.post<ApiResponse<SendResult>>(
      `/billing/clients/${id}/send-invoice`,
      payload,
      { timeout: 60_000, params: { orderId } },
    );
    if (!data.data) throw new Error('Send result not found in response');
    return data.data;
  },

  complete: async (id: number, orderId?: number): Promise<BillingClientDetail> => {
    const { data } = await apiClient.post<ApiResponse<BillingClientDetail>>(
      `/billing/clients/${id}/complete`,
      undefined,
      { params: { orderId } },
    );
    if (!data.data) throw new Error('Billing client not found in response');
    return data.data;
  },

  downloadInvoicePdf: async (id: number, orderId?: number): Promise<void> => {
    const response = await apiClient.get<Blob>(`/billing/clients/${id}/invoice-pdf`, {
      responseType: 'blob',
      params: { orderId },
    });
    const disposition = response.headers['content-disposition'] as string | undefined;
    const match = disposition?.match(/filename="?([^"]+)"?/);
    const filename = match?.[1] ?? `invoice-${id}.pdf`;
    const blobUrl = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  },
};
